import { Grid, TextField } from '@mui/material';
import React, { forwardRef, useEffect, useImperativeHandle, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { UIExternalProfile } from '@api/v1';
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator';
import { useHistory } from 'react-router';
import { State } from '../../reducers';
import { fetchRatings } from '../../profile/actions';
import { RouteLeavingGuard } from '../../../components/RouteLeavingGuard';
import ContentCard from '../../../mui-custom/ContentCard/ContentCard';
import { saveExternalProfileDetails } from '../account/actions';

/*
this regex allows the following url pattern:
casavi.de
casavi.de/page1
www.casavi.de/page1
http://casavi.de
http://casavi.de/page1
http://www.casavi.de
http://www.casavi.de/page1
https://www.casavi.de
https://www.casavi.de/page1
*/
export const websiteRegex =
	'[(http(s)?):\\/\\/(www\\.)?a-zA-Z0-9@:%._\\+~#=]{2,256}\\.[a-z]{2,6}\\b([-a-zA-Z0-9@:%_\\+.~#?&//=]*)';

interface AdminExternalProfileAppV3Props {
	onEditingChange?: (isEditing: boolean) => void;
}

export interface AdminExternalProfileAppV3Handle {
	isValid: () => Promise<boolean>;
	submit: () => Promise<void>;
	reset: () => void;
}

export const AdminExternalProfileAppV3 = forwardRef<
	AdminExternalProfileAppV3Handle,
	AdminExternalProfileAppV3Props
>(function AdminExternalProfileAppV3({ onEditingChange }, ref) {
	const { user } = useSelector((state: State) => state.auth);
	const { account } = useSelector((state: State) => state.admin.account);
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const history = useHistory();

	const formRef = useRef<any>(null);

	const initialFormState: UIExternalProfile = {
		name: account?.externalProfile?.name || account?.name || '',
		email: account?.externalProfile?.email || user?.email || '',
		address: account?.externalProfile?.address || account?.address,
		companyDescription: account?.externalProfile?.companyDescription || '',
		isActive: account?.externalProfile?.isActive || false,
		companyWebsite: account?.externalProfile?.companyWebsite || ''
	};
	const [formData, setFormData] = useState(initialFormState);
	const [isEditing, setIsEditing] = useState(false);

	const resetForm = () => {
		setIsEditing(false);
		onEditingChange?.(false);
		setFormData(initialFormState);
	};

	const handleFormSubmit = () => {
		setIsEditing(false);
		onEditingChange?.(false);
		dispatch(saveExternalProfileDetails({ externalProfile: formData }));
	};

	useImperativeHandle(ref, () => ({
		isValid: () => {
			if (!formRef.current?.isFormValid) {
				return Promise.resolve(true);
			}

			return formRef.current.isFormValid(true);
		},
		submit: () => {
			handleFormSubmit();
			return Promise.resolve();
		},
		reset: () => {
			resetForm();
		}
	}));

	useEffect(() => {
		setFormData(initialFormState);
		if (account?.externalProfile) dispatch(fetchRatings());
	}, [account]);

	return (
		<ContentCard headline={t('general:company_data')} text={''}>
			<ValidatorForm
				ref={formRef}
				onSubmit={handleFormSubmit}
				onFocus={() => {
					setIsEditing(true);
					onEditingChange?.(true);
				}}
				style={{
					display: 'flex',
					flexDirection: 'column',
					gap: 16
				}}
			>
				<TextValidator
					label={t('general:company_name')}
					validators={['required']}
					value={formData.name}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						setFormData({ ...formData, name: e.target.value })
					}
					errorMessages={[t('general:required_message')]}
					fullWidth
					variant="outlined"
					name="companyNameInput"
				/>
				<TextValidator
					label={t('general:streetAddress')}
					name="companyStreetInput"
					validators={['required']}
					value={formData.address?.streetAddress || ''}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						setFormData({
							...formData,
							address: {
								...formData.address,
								streetAddress: e.target.value
							}
						})
					}
					errorMessages={[t('general:required_message')]}
					fullWidth
					variant="outlined"
				/>
				<Grid sx={{ display: 'flex', gap: 2 }}>
					<TextValidator
						label={t('general:postal_code')}
						name="companyPostInput"
						validators={['required']}
						value={formData.address?.postalCode || ''}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
							setFormData({
								...formData,
								address: {
									...formData.address,
									postalCode: e.target.value
								}
							})
						}
						errorMessages={[t('general:required_message')]}
						sx={{ width: '88px' }}
						variant="outlined"
					/>
					<Grid sx={{ flex: 1 }}>
						<TextValidator
							label={t('general:locality')}
							name="companyCityInput"
							validators={['required']}
							value={formData.address?.locality || ''}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								setFormData({
									...formData,
									address: {
										...formData.address,
										locality: e.target.value
									}
								})
							}
							errorMessages={[t('general:required_message')]}
							fullWidth
							variant="outlined"
						/>
					</Grid>
				</Grid>
				<TextValidator
					label={t('general:email_address')}
					name="companyEMailInput"
					validators={['required', 'isEmail']}
					errorMessages={[t('general:required_message'), t('validation:invalid_email_address')]}
					value={formData.email}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						setFormData({ ...formData, email: e.target.value })
					}
					fullWidth
					variant="outlined"
				/>
				<TextValidator
					label={t('general:website')}
					name="companySiteInput"
					validators={[`matchRegexp:${websiteRegex}`]}
					value={formData.companyWebsite}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						setFormData({
							...formData,
							companyWebsite: e.target.value
						})
					}
					errorMessages={[t('general:no_valid_link')]}
					fullWidth
					variant="outlined"
				/>
				<TextField
					multiline
					rows={6}
					label={t('general:company_description')}
					value={formData.companyDescription}
					onChange={(e) =>
						setFormData({
							...formData,
							companyDescription: e.target.value
						})
					}
					fullWidth
					variant="outlined"
				/>
				<RouteLeavingGuard
					navigate={(path) => history.push(path)}
					shouldBlockNavigation={(_location) => isEditing}
					title={t('profile:discard_changes')}
					confirmButtonText={t('general:cta_discard_edit')}
				/>
			</ValidatorForm>
		</ContentCard>
	);
});
